import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'

import classnames from 'classnames'
import { Form, Input, Select, Button, Space } from 'antd'
import ChatLayout from '@/components/ChatLayout'
import ModeSelect from '@/components/form/ModeSelect'
import useReducers from '@/store/translate'
import http from '@/utils/request'
import topup from '@/utils/topup'
import TextRender from '@/components/TextRender'
import Expense from '@/components/Expense'
import CreationDemo from '@/components/Demo/CreationDemo'
import tryParse from '@/utils/tryParse'

import ArticlePrompt from '@/components/ArticlePrompt'
import styles from './translate.module.scss'

export default React.memo(function Translate() {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [state, actions] = useReducers()
  const lastRef = useRef()

  const messages = useMemo(() => {
    return state.messages[state.active] || []
  }, [state.messages, state.active])

  useEffect(() => {
    if (messages.length === 0) {
      form.resetFields()
      return
    }

    const last = messages[messages.length - 1]
    const { extra } = last
    form.setFieldsValue({
      ...extra,
      model: extra?.model || 'gpt-3.5'
    })
  }, [messages])

  useEffect(() => {
    setTimeout(() => {
      if (lastRef.current) {
        lastRef.current.scrollIntoView()
      }
    }, 200)
  }, [messages])

  useEffect(() => {
    if (!state.active) {
      return
    }

    const chatId = state.active

    http.get(`/api/chat/${state.active}`).then(res => {
      if (!res) {
        return
      }

      actions.setMessages({
        [chatId]: res
      })
    })
  }, [state.active])

  const submit = useCallback(async ({ content, ...extra }) => {
    const chatId = state.active

    const lastMessage = {
      content: content,
      id: Math.random(),
      extra
    }

    actions.setMessages({
      [chatId]: [...messages, { ...lastMessage }]
    })

    try {
      await http.post('/api/chat/translate/create', {
        chatId: state.active,
        extra: extra,
        content: content,
      }, {
        onDownloadProgress: function ({ event }) {
          const xhr = event.target
          const { responseText } = xhr
          if (xhr.status !== 200) {
            return
          }

          const jsonText = `[${responseText.replace(/\}\{/g, '},{')}]`
          const json = tryParse(jsonText)

          if (!json) {
            return
          }

          const translated = json.reduce((result, next) => {
            return `${result}${next.data}`
          }, '')

          lastMessage.translated = translated

          actions.setMessages({
            [chatId]: [...messages, { ...lastMessage }]
          })
        },
      })

      const chatDetail = await http.get(`/api/chat/${chatId}`)

      actions.setMessages({
        [chatId]: chatDetail
      })
    } catch(err) {
      actions.setMessages({ [chatId]: messages })
      if (err.response?.data.code === '1001') {
        topup(err.response.data.message)
      }
    }

  }, [messages, state.active])

  const languageOptions = useMemo(() => {
    return [{ value: '默认', label: '翻译成英语'}, '英语', '英式英语', '美试英语', '中文', '法语','俄语','日语','韩文','泰文','德语'].map(item => {
      if (typeof item === 'string') {
        return { value: item, label: item}
      }
      return item
    })
  }, [])

  return (
    <ChatLayout
      type="translate"
      title="翻译"
      onActiveChange={actions.setActive}
      histories={state.histories}
      active={state.active}
      actions={actions}
    >
      <div className={styles.container}>
        <div className={styles.messages}>
          {
            messages.map((item, index) => {
              return (
                <>
                  <div key={`${item.id}-user`} className={classnames(styles.item, styles.user)}>
                    <TextRender text={item.content}/>
                    <div style={{ textAlign: 'right'}}>
                      <Expense content={item.content} expense={item.expense}/>
                    </div>
                  </div>
                  <div key={`${item.id}-assistant`} className={classnames(styles.item, styles.assistant)}>
                    {
                      !!item.translated && <TextRender text={item.translated}/>
                    }
                    {
                      !item.translated && <TextRender text={'正在回复...'}/>
                    }
                  </div>
                </>
              )
            })
          }
          <div ref={lastRef}></div>
        </div>

        <div className={styles.footer}>
          <ArticlePrompt
            contentProps={{ placeholder: '请输入需要翻译的内容（如：早上好）', name: 'content', maxLength: 1000 }}
            form={form}
            onSubmit={submit}
            extra={
              <Space>
                <Form.Item name="model">
                  <ModeSelect/>
                </Form.Item>
                <Form.Item name="language">
                  <Select options={languageOptions} size="middle" dropdownMatchSelectWidth={false}/>
                </Form.Item>
              </Space>
            }
          />
        </div>
      </div>
    </ChatLayout>
  )
})
